import { useState, useEffect } from 'react';
import { useJobs } from '../hooks/useJobs';
import { jobService } from '../services/jobService';
import PropTypes from 'prop-types';

const JobModal = ({ show, onHide, onSave, job, suggestedJobNumber, saveError, existingJobs }) => {
  const [formData, setFormData] = useState({
    job_number: job?.job_number || suggestedJobNumber || '',
    name: job?.name || '',
    description: job?.description || '',
    status: job?.status || 'active',
    start_date: job?.start_date || '',
    end_date: job?.end_date || ''
  });
  const [isDuplicate, setIsDuplicate] = useState(false);

  useEffect(() => {
    setFormData({
      job_number: job?.job_number || suggestedJobNumber || '',
      name: job?.name || '',
      description: job?.description || '',
      status: job?.status || 'active',
      start_date: job?.start_date || '',
      end_date: job?.end_date || ''
    });
    setIsDuplicate(false);
  }, [job, suggestedJobNumber]);

  // Check for duplicate job number
  useEffect(() => {
    if (!formData.job_number || !existingJobs) {
      setIsDuplicate(false);
      return;
    }

    // Check if job number already exists (excluding current job being edited)
    const duplicate = existingJobs.some(
      existingJob => existingJob.job_number === formData.job_number && existingJob.id !== job?.id
    );
    setIsDuplicate(duplicate);
  }, [formData.job_number, existingJobs, job]);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Clean up empty date strings
    const cleanedData = {
      ...formData,
      start_date: formData.start_date || null,
      end_date: formData.end_date || null,
      description: formData.description || null
    };
    
    // Include job_number if it's provided
    if (formData.job_number) {
      cleanedData.job_number = formData.job_number;
    }
    
    onSave(cleanedData);
  };

  if (!show) return null;

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{job ? 'Edit Job' : 'New Job'}</h5>
            <button type="button" className="btn-close" onClick={onHide}></button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {saveError && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                  {saveError}
                </div>
              )}
              <div className="mb-3">
                <label className="form-label">
                  Job Number *
                  <small className="text-muted ms-2">(Format: YYYYXXX)</small>
                </label>
                <input
                  type="text"
                  className={`form-control ${isDuplicate ? 'is-invalid' : ''}`}
                  value={formData.job_number}
                  onChange={(e) => setFormData({ ...formData, job_number: e.target.value })}
                  pattern="\d{7}"
                  maxLength="7"
                  title="Job number must be 7 digits in format YYYYXXX"
                  required
                />
                {isDuplicate && (
                  <div className="invalid-feedback d-block">
                    <i className="bi bi-exclamation-triangle-fill me-1"></i>
                    This job number already exists. Please use a different number.
                  </div>
                )}
                {!isDuplicate && !job && <small className="form-text text-muted">Next suggested: {suggestedJobNumber}</small>}
                {!isDuplicate && job && <small className="form-text text-muted">Change with caution - must remain unique</small>}
              </div>
              <div className="mb-3">
                <label className="form-label">Name *</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Status *</label>
                <select
                  className="form-select"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  required
                >
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Start Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">End Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  />
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label">Description</label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onHide}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={isDuplicate}>
                {isDuplicate ? 'Duplicate Job Number' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

JobModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  suggestedJobNumber: PropTypes.string,
  saveError: PropTypes.string,
  existingJobs: PropTypes.array,
  job: PropTypes.shape({
    id: PropTypes.number,
    job_number: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    status: PropTypes.string,
    start_date: PropTypes.string,
    end_date: PropTypes.string
  })
};

const JobsPage = () => {
  const [statusFilter, setStatusFilter] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { jobs, loading, error, createJob, updateJob, deleteJob } = useJobs(statusFilter, searchTerm);
  const [showModal, setShowModal] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [suggestedJobNumber, setSuggestedJobNumber] = useState('');
  const [saveError, setSaveError] = useState(null);

  const fetchSuggestedJobNumber = async () => {
    try {
      const nextJobNumber = await jobService.getNextJobNumber();
      setSuggestedJobNumber(nextJobNumber);
    } catch (err) {
      console.error('Failed to fetch suggested job number:', err);
    }
  };

  const handleOpenNewJobModal = () => {
    setEditingJob(null);
    setSaveError(null);
    fetchSuggestedJobNumber();
    setShowModal(true);
  };

  const handleSave = async (formData) => {
    setSaveError(null);
    try {
      if (editingJob) {
        await updateJob(editingJob.id, formData);
      } else {
        await createJob(formData);
      }
      setShowModal(false);
      setEditingJob(null);
    } catch (err) {
      console.error('Failed to save job:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to save job. Please try again.';
      setSaveError(errorMessage);
    }
  };

  const handleEdit = (job) => {
    setEditingJob(job);
    setSaveError(null);
    setShowModal(true);
  };

  const handleDelete = async (jobId) => {
    if (window.confirm('Are you sure you want to delete this job?')) {
      try {
        await deleteJob(jobId);
      } catch (err) {
        console.error('Failed to delete job:', err);
      }
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'bg-success',
      completed: 'bg-secondary',
      cancelled: 'bg-danger'
    };
    return badges[status] || 'bg-secondary';
  };

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Construction Jobs</h2>
        <button 
          className="btn btn-primary"
          onClick={handleOpenNewJobModal}
        >
          <i className="bi bi-plus-circle me-2"></i>
          New Job
        </button>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
        </div>
      )}

      <div className="card">
        <div className="card-body">
          <div className="row mb-3">
            <div className="col-md-6">
              <input
                type="text"
                className="form-control"
                placeholder="Search jobs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <select
                className="form-select"
                value={statusFilter || ''}
                onChange={(e) => setStatusFilter(e.target.value || null)}
              >
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Job Number</th>
                    <th>Name</th>
                    <th>Status</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th width="150">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center text-muted py-4">
                        No jobs found
                      </td>
                    </tr>
                  ) : (
                    jobs.map(job => (
                      <tr key={job.id}>
                        <td><strong>{job.job_number}</strong></td>
                        <td>{job.name}</td>
                        <td>
                          <span className={`badge ${getStatusBadge(job.status)}`}>
                            {job.status.charAt(0).toUpperCase() + job.status.slice(1)}
                          </span>
                        </td>
                        <td>{job.start_date || <span className="text-muted">—</span>}</td>
                        <td>{job.end_date || <span className="text-muted">—</span>}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-primary me-2"
                            onClick={() => handleEdit(job)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(job.id)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <JobModal
        show={showModal}
        onHide={() => {
          setShowModal(false);
          setEditingJob(null);
          setSaveError(null);
        }}
        onSave={handleSave}
        job={editingJob}
        suggestedJobNumber={suggestedJobNumber}
        saveError={saveError}
        existingJobs={jobs}
      />
    </div>
  );
};

export default JobsPage;
